import dayjs from 'dayjs'
import React, {FC, useEffect, useRef, useState} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {useLocation} from 'react-router'
import {HomeNotificationRefetchProvider} from 'src/app/context/HomeNotificationContext'
import {MasterProvider} from 'src/app/context/MasterContext'
import {PageDataProvider} from 'src/app/context/PageDataContext'
import {HeaderToastProvider} from 'src/app/context/ToastContext'
import AuthRedux from 'src/app/store/Auth/AuthRedux'
import {RootState} from 'src/app/store/RootReducer'
import {HeaderToast} from '../Libs'
import SessionModal from '../Widgets/ModalCollection/SessionModal'
import ContentWrapper from './ContentWrapper'
import ScrollToTop from './ScrollToTop'
import TooltipView from './TooltipView'
import {AsideWrapper} from './aside/AsideWrapper'
import {Footer} from './footer/Footer'
import HeaderWrapper from './header/HeaderWrapper'
import OverlayWrapper from './overlay/OverlayWrapper'

const MasterLayout: FC<{children?: React.ReactNode}> = ({children}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const {pathname} = useLocation()

  const isEndSession = useSelector<RootState>(({auth}) => auth?.sessionEnded, shallowEqual)
  const dispatch = useDispatch()

  const [showModalSession, setShowModalSession] = useState(false)

  useEffect(() => {
    if (isEndSession) {
      setTimeout(() => setShowModalSession(true), 500)
    }
  }, [isEndSession])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
      window.scrollTo(0, 0)
    }
  }, [pathname])

  useEffect(() => {
    const sessionKey = 'session_timeout'
    const existingTimeout = localStorage.getItem(sessionKey)

    if (!isEndSession) return

    if (!existingTimeout) {
      const timeout = dayjs().add(1, 'minute').toISOString()
      localStorage.setItem(sessionKey, timeout)
    }
  }, [isEndSession])

  // Auto logout when session has expired
  useEffect(() => {
    const sessionKey = 'session_timeout'
    const timeout = localStorage.getItem(sessionKey)

    if (!timeout) return

    const checkInterval = setInterval(() => {
      const storedTimeout = localStorage.getItem(sessionKey)
      if (storedTimeout && dayjs().isAfter(dayjs(storedTimeout))) {
        localStorage.removeItem(sessionKey)
        dispatch(AuthRedux.actions.logout())
      }
    }, 5000)

    return () => clearInterval(checkInterval)
  }, [dispatch])

  return (
    <MasterProvider>
      <PageDataProvider>
        <HeaderToastProvider>
          <HomeNotificationRefetchProvider>
            <OverlayWrapper />

            <div className='pt-[80px] flex flex-row flex-[1_0_auto] bg-neutral-25 transition-all duration-300'>
              <AsideWrapper />

              <ContentWrapper>
                <HeaderWrapper />
                <HeaderToast />

                <div ref={scrollRef} className='py-6 flex flex-col flex-[1_0_auto]'>
                  <div className='flex flex-[1_0_auto]'>
                    <div className='w-full px-8 mx-auto'>{children}</div>
                  </div>
                </div>

                <Footer />
              </ContentWrapper>
            </div>

            <SessionModal
              show={showModalSession}
              setShow={() => {}}
              onSubmit={() => dispatch(AuthRedux.actions.logout())}
            />
          </HomeNotificationRefetchProvider>
        </HeaderToastProvider>
      </PageDataProvider>
      <ScrollToTop />
      <TooltipView />
    </MasterProvider>
  )
}

export default MasterLayout
